import { createTheme, type CSSVariablesResolver, type MantineThemeOverride } from '@mantine/core';

/**
 * テーマは1箇所に集約する。
 *
 * 固定 px を使わず `rem` で組み、中央ディスプレイ用には `scale` を上げた派生テーマを
 * 使う。これで画面サイズの異なる中央端末でも、離れた位置から読める文字サイズになる
 * （要件11.9）。
 */

const shared = {
  primaryColor: 'teal',
  defaultRadius: 'md',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Hiragino Sans", "Noto Sans JP", "Yu Gothic UI", sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
  headings: {
    fontWeight: '700',
  },
  // 金額は等幅の数字で揃える。桁が動いても視線がぶれない。
  other: {
    numericFontFeature: '"tnum" 1',
  },
} satisfies MantineThemeOverride;

/** プレイヤー端末（スマホ縦向き、片手の親指操作）。 */
export const playerTheme = createTheme({
  ...shared,
  // 親指で押し損じない大きさを既定にする。
  components: {
    Button: {
      defaultProps: { size: 'lg' },
    },
    NumberInput: {
      defaultProps: { size: 'lg' },
    },
  },
});

/** 中央ディスプレイ。離れた位置からの視認性を優先して全体を拡大する。 */
export const displayTheme = createTheme({
  ...shared,
  scale: 1.15,
});

/**
 * 既定より高いコントラストにする。両方のテーマで共通に使う。
 *
 * Mantine のダークテーマは補助的な文字（`c="dimmed"`）を dark-2 で描く。卓の暗い背景と
 * 明るい teal のグラデーションが混在する画面では、この明度では小さな文字が読み取れない。
 * 中央ディスプレイは離れた位置から読む前提なので（要件11.9）、補助的な文字にも十分な
 * コントラストを与える。
 *
 * 枠線も既定の dark-4 では席カードの輪郭が背景に溶ける。卓の配置を読み取るには輪郭が
 * 見えている必要がある。
 *
 * 色は WCAG AA（4.5:1）を超える組み合わせだけを使う。gray-5 は dark-9 の背景に対して
 * 7:1 を上回る。
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {},
  dark: {
    '--mantine-color-text': 'var(--mantine-color-gray-0)',
    '--mantine-color-dimmed': 'var(--mantine-color-gray-5)',
    '--mantine-color-placeholder': 'var(--mantine-color-gray-5)',
    '--mantine-color-default-border': 'var(--mantine-color-dark-3)',
  },
});
